import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
    Button,
    DropdownMenu,
    Input,
    ScrollBar,
    SparkModalPortal,
    sparkToast,
} from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { getTaskTypeShortLabel } from '@/features/tasks/utils/types';
import LanguageSelector from '../LanguageSelector';
import { sanitizeProjectName, sanitizeTaskName } from './projectNameUtils';
import { TaskLabelsField } from '../CreationWizard/shared/TaskTypeControls';
import PriorityRanking from '../CreationWizard/businessGoals/PriorityRanking';
import ConstraintsEditor from '../CreationWizard/businessGoals/ConstraintsEditor';
import FallbackLabelSelect from '../CreationWizard/businessGoals/FallbackLabelSelect';
import { fallbackLabelFromStored, fallbackPayloadFromSelect } from '../CreationWizard/businessGoals/fallbackLabel';
import {
    DEFAULT_PILLAR_ORDER,
    collectConstraintErrors,
    constraintDuplicateMetricErrors,
    constraintsFromBackend,
    constraintsToPayload,
    DUPLICATE_CONSTRAINT_TOAST,
} from '../CreationWizard/businessGoals/constraintOptions';
import '../CreationWizard/creation-wizard.css';
import './EditTaskModal.css';

/**
 * Labels shown for a task: the ones the user typed in Step 1
 * (`manually_added_*`), falling back to the labels observed in the task's
 * datasets (`*_names_count` keys) so tasks whose labels came from data still
 * display them.
 */
const displayLabelsForTask = (task) => {
    if (!task) return [];
    const baseType = task.task_type === TaskType.NER_WITH_RELATIONS
        ? TaskType.NER
        : task.task_type;
    if (baseType === TaskType.NER) {
        const manual = task.manually_added_entities || [];
        return manual.length ? manual : Object.keys(task.entity_names_count || {});
    }
    if (baseType === TaskType.classification) {
        const manual = task.manually_added_labels || [];
        return manual.length ? manual : Object.keys(task.label_names_count || {});
    }
    return [];
};

/**
 * Duplicate Task modal (Spark) — mirrors the Edit Task modal layout.
 *
 * Editable: task name, project, task languages, description, fallback label,
 * business-goal priorities, and constraints. Everything else (task type,
 * single/multi-label, labels, datasets, prompt/teacher settings, and the
 * "When evaluating" cleanup flags) is copied read-only from the source task.
 *
 * Composite tasks (multiple subtasks sharing one `sub_group_name`) hide the
 * per-subtask sections entirely — each subtask keeps its own type, labels,
 * fallback, goals, and constraints unchanged; only the shared name, project,
 * languages, and description are edited.
 */
export const DuplicateTaskModal = ({
    isOpen,
    onClose,
    originalTask,
    allProjects = [],
    onDuplicateTask
}) => {
    const [taskName, setTaskName] = useState('');
    const [taskDescription, setTaskDescription] = useState('');
    const [taskNameError, setTaskNameError] = useState(false);
    const [taskLanguages, setTaskLanguages] = useState(['en']);
    const [taskLanguagesError, setTaskLanguagesError] = useState(false);
    const [selectedProjectName, setSelectedProjectName] = useState('');
    const [newProjectName, setNewProjectName] = useState('');
    const [projectNameError, setProjectNameError] = useState(false);
    const [fallbackLabel, setFallbackLabel] = useState(null);
    const [fallbackLabelError, setFallbackLabelError] = useState(null);
    const [priorityOrder, setPriorityOrder] = useState([...DEFAULT_PILLAR_ORDER]);
    const [constraints, setConstraints] = useState([]);
    const [constraintErrors, setConstraintErrors] = useState(null);
    const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);

    // Scrollable dialog body — tracked by the Spark ScrollBar, which is a
    // sibling of this node (same pattern as the Edit Task modal).
    const scrollBodyRef = useRef(null);
    const taskNameInputRef = useRef(null);
    // Copied name to focus once it is in the input. Set on the open
    // transition and consumed by the layout effect below.
    const pendingTaskNameFocusRef = useRef(null);
    const wasOpenRef = useRef(false);
    if (!isOpen) {
        wasOpenRef.current = false;
        pendingTaskNameFocusRef.current = null;
    } else if (originalTask && !wasOpenRef.current) {
        pendingTaskNameFocusRef.current = sanitizeTaskName(
            `${originalTask.usecase_name}(Copy)`
        );
        wasOpenRef.current = true;
    }

    const projectNames = [...new Set(
        (allProjects || [])
            .map((p) => p?.group_name)
            .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));

    const originalSubTasks = useMemo(() => (
        Array.isArray(originalTask?.usecases) && originalTask.usecases.length > 0
            ? originalTask.usecases
            : (originalTask ? [originalTask] : [])
    ), [originalTask]);
    const isCompositeTask = originalSubTasks.length > 1;
    const sourceTask = originalSubTasks[0] || null;

    // Read-only type/labels derived from the (single) source task.
    const baseTaskType = sourceTask?.task_type === TaskType.NER_WITH_RELATIONS
        ? TaskType.NER
        : (sourceTask?.task_type || TaskType.classification);
    const isClassification = baseTaskType === TaskType.classification;
    const isNer = baseTaskType === TaskType.NER;
    const classificationType = isClassification
        ? (sourceTask?.is_multilabel ? 'multi' : 'single')
        : null;
    const displayLabels = useMemo(
        () => (isCompositeTask ? [] : displayLabelsForTask(sourceTask)),
        [isCompositeTask, sourceTask]
    );
    // Fallback options: display labels plus the stored rejection label, so the
    // select can always render the source task's current choice.
    const fallbackOptionLabels = useMemo(() => {
        const rejection = sourceTask?.rejection_label_name;
        if (rejection && !displayLabels.includes(rejection)) {
            return [...displayLabels, rejection];
        }
        return displayLabels;
    }, [displayLabels, sourceTask]);

    const showTypeFacts = !isCompositeTask;
    const showLabelsSection =
        !isCompositeTask && (isClassification || isNer) && displayLabels.length > 0;
    const showFallbackSection =
        !isCompositeTask && isClassification && fallbackOptionLabels.length > 0;
    const showGoalsSection = !isCompositeTask;

    // Seed the form from the source task each time the modal opens.
    useEffect(() => {
        if (isOpen && originalTask) {
            setTaskName(sanitizeTaskName(`${originalTask.usecase_name}(Copy)`));
            setTaskDescription(originalTask.usecase_goal || '');
            setTaskNameError(false);
            const languageCodes = originalSubTasks
                .flatMap((task) => task.task_languages || [])
                .filter(Boolean);
            const uniqueLanguageCodes = Array.from(new Set(languageCodes));
            setTaskLanguages(uniqueLanguageCodes.length > 0 ? uniqueLanguageCodes : ['en']);
            setTaskLanguagesError(false);
            setSelectedProjectName(originalTask.group_name || '');
            setNewProjectName('');
            setProjectNameError(false);
            const first = originalSubTasks[0];
            const firstBaseType = first?.task_type === TaskType.NER_WITH_RELATIONS
                ? TaskType.NER
                : first?.task_type;
            setFallbackLabel(
                fallbackLabelFromStored({
                    isClassification: firstBaseType === TaskType.classification,
                    isMultilabel: Boolean(first?.is_multilabel),
                    rejectionLabelName: first?.rejection_label_name,
                    confidenceRejectionEnabled: first?.confidence_rejection_enabled,
                })
            );
            setFallbackLabelError(null);
            setPriorityOrder(
                first?.business_goal_priorities?.length
                    ? [...first.business_goal_priorities]
                    : [...DEFAULT_PILLAR_ORDER]
            );
            setConstraints(constraintsFromBackend(first?.constraints));
            setConstraintErrors(null);
        }
    }, [isOpen, originalTask, originalSubTasks]);

    // When the modal opens, put the caret at the end of the copied name.
    // The name is seeded in an effect, so this waits until that value is in
    // the input. The dialog focus trap leaves a control that is already
    // focused inside the dialog alone.
    useLayoutEffect(() => {
        const targetName = pendingTaskNameFocusRef.current;
        if (!isOpen || targetName == null) return;
        const input = taskNameInputRef.current;
        if (!input || input.value !== targetName) return;
        pendingTaskNameFocusRef.current = null;
        input.focus();
        const end = input.value.length;
        input.setSelectionRange(end, end);
    }, [isOpen, taskName]);

    const resetForm = () => {
        setTaskName('');
        setTaskDescription('');
        setTaskNameError(false);
        setTaskLanguages(['en']);
        setTaskLanguagesError(false);
        setSelectedProjectName('');
        setNewProjectName('');
        setProjectNameError(false);
        setFallbackLabel(null);
        setFallbackLabelError(null);
        setPriorityOrder([...DEFAULT_PILLAR_ORDER]);
        setConstraints([]);
        setConstraintErrors(null);
    };

    const handleDuplicateTask = () => {
        const hasEmptyTaskName = !taskName.trim();
        const hasEmptyProjectName = !selectedProjectName || !selectedProjectName.trim();
        const hasEmptyLanguages = !taskLanguages || taskLanguages.length === 0;
        const hasEmptyFallback = showFallbackSection && !fallbackLabel;

        const nextConstraintErrors = showGoalsSection
            ? collectConstraintErrors(constraints)
            : {};
        const hasConstraintErrors = Object.keys(nextConstraintErrors).length > 0;

        setTaskNameError(hasEmptyTaskName);
        setProjectNameError(hasEmptyProjectName);
        setTaskLanguagesError(hasEmptyLanguages);
        setFallbackLabelError(
            hasEmptyFallback ? 'Please select a fallback label' : null
        );
        setConstraintErrors(hasConstraintErrors ? nextConstraintErrors : null);

        if (
            hasEmptyTaskName ||
            hasEmptyProjectName ||
            hasEmptyLanguages ||
            hasEmptyFallback ||
            hasConstraintErrors
        ) {
            if (
                hasConstraintErrors &&
                Object.keys(constraintDuplicateMetricErrors(constraints)).length
            ) {
                sparkToast.error(DUPLICATE_CONSTRAINT_TOAST.title, {
                    content: DUPLICATE_CONSTRAINT_TOAST.content,
                    detailed: true,
                    duration: 5000,
                });
            }
            return;
        }

        const newUsecaseName = sanitizeTaskName(taskName, { trimEnd: true });
        const selectedGroupName = sanitizeProjectName(selectedProjectName, { trimEnd: true }).trim();
        const duplicatedTasks = originalSubTasks.map((task) => {
            // Rejection fields: taken from the fallback select when it is
            // shown; otherwise copied unchanged from the source subtask.
            const rejectionFields = showFallbackSection
                ? fallbackPayloadFromSelect(fallbackLabel, {
                    isClassification,
                    classificationType,
                })
                : {
                    confidence_rejection_enabled: task.confidence_rejection_enabled,
                    rejection_label_name: task.rejection_label_name,
                };

            return {
                usecase_name: isCompositeTask ? task.usecase_name : newUsecaseName,
                usecase_goal: taskDescription,
                // Task type is read-only: copied unchanged (NER with
                // relations included).
                task_type: task.task_type,
                // `sub_group_name` is the Task identifier (required server-side).
                // A duplicated row defaults to its own standalone Task — keep the
                // value in sync with the new usecase_name so the duplicate does
                // not silently merge into the source's multi-objective Task and create
                // a JSON-output-key collision.
                sub_group_name: newUsecaseName,
                datasets: task.datasets,
                benchmarks: task.benchmarks,
                manual_model_predictions: task.manual_model_predictions,
                source_usecase_id: task._id,
                prompt: task.prompt,
                teacher_model: task.teacher_model,
                teacher_provider: task.teacher_provider,
                parser_function: task.parser_function,
                comparison_mode: task.comparison_mode,
                comparison_mode_option: task.comparison_mode_option,
                group_name: selectedGroupName,
                client_id: task.client_id,
                label_names_count: task.label_names_count,
                entity_names_count: task.entity_names_count,
                relation_names_count: task.relation_names_count,
                benchmark_relation_names_count: task.benchmark_relation_names_count,
                benchmark_label_names_count: task.benchmark_label_names_count,
                benchmark_entity_names_count: task.benchmark_entity_names_count,
                tool_names_count: task.tool_names_count,
                benchmark_tool_names_count: task.benchmark_tool_names_count,
                long_text_count: task.long_text_count,
                code_example_count: task.code_example_count,
                is_multilabel: task.is_multilabel,
                classify_speakers: task.classify_speakers,
                ...rejectionFields,
                task_languages: taskLanguages || [],
                judge_model_identifier: task.judge_model_identifier,
                user_criteria_prompts: task.user_criteria_prompts,
                mandatory_criteria_prompts: task.mandatory_criteria_prompts,
                llm_judge_enabled_by_id: task.llm_judge_enabled_by_id,
                label_descriptions: task.label_descriptions,
                // Expected labels typed in Step 1 — copied so the duplicate
                // keeps them (the legacy modal silently dropped these).
                manually_added_labels: task.manually_added_labels,
                manually_added_entities: task.manually_added_entities,
                // "When evaluating" flags — copied so the duplicate keeps the
                // same checked/unchecked cleanup options.
                evaluation_normalize_casing: Boolean(task.evaluation_normalize_casing),
                evaluation_strip_whitespace: Boolean(task.evaluation_strip_whitespace),
                evaluation_fix_json_format: Boolean(task.evaluation_fix_json_format),
                // Business goals: edited values for atomic tasks; each
                // subtask keeps its own for composite tasks.
                business_goal_priorities: showGoalsSection
                    ? priorityOrder
                    : (task.business_goal_priorities || []),
                constraints: showGoalsSection
                    ? constraintsToPayload(constraints)
                    : (task.constraints || []),
            };
        });

        onDuplicateTask(isCompositeTask ? duplicatedTasks : duplicatedTasks[0]);

        resetForm();
        onClose();
    };

    const handleCancel = () => {
        onClose();
        resetForm();
    };

    const selectProject = (name) => {
        setSelectedProjectName(name);
        setProjectNameError(false);
        setProjectDropdownOpen(false);
    };

    const createProjectFromInput = () => {
        const v = sanitizeProjectName((newProjectName || '').trim(), { trimEnd: true });
        if (!v) return;
        setSelectedProjectName(v);
        setNewProjectName('');
        setProjectNameError(false);
        setProjectDropdownOpen(false);
    };

    if (!originalTask) return null;

    return (
        <SparkModalPortal
            open={isOpen}
            onClose={handleCancel}
            size="wide"
            destroyOnClose
            className="edit-task-modal-portal"
        >
            <div className="edit-task-modal-shell">
                <div className="edit-task-modal" ref={scrollBodyRef}>
                    <div className="cw-step cw-step--business-goals fs-unmask">
                        <div className="cw-title-row">
                            <div className="cw-title-row__fields">
                                <div className="edit-task-modal__name-row">
                                    <input
                                        ref={taskNameInputRef}
                                        className={`cw-task-name-input${taskNameError ? ' cw-task-name-input--error' : ''}`}
                                        placeholder="Task name"
                                        value={taskName}
                                        onChange={(e) => {
                                            setTaskName(sanitizeTaskName(e.target.value));
                                            setTaskNameError(false);
                                        }}
                                        aria-label="Task name"
                                    />
                                </div>
                                {taskNameError && (
                                    <div className="cw-field-error">Task name is required</div>
                                )}
                                <input
                                    className="cw-task-description-input"
                                    placeholder="No task description provided."
                                    value={taskDescription}
                                    onChange={(e) => setTaskDescription(e.target.value)}
                                    aria-label="Task description"
                                />
                            </div>
                        </div>

                        <div className="duplicate-task-modal__intro">
                            <div>
                                <h3 className="cw-goals-section__title">Duplicate Task</h3>
                                <p className="cw-goals-section__subtitle">
                                    Create a copy of "<b>{originalTask.usecase_name}</b>"
                                    {isCompositeTask ? ` with ${originalSubTasks.length} sub tasks` : ''}.
                                </p>
                            </div>

                            <div className="duplicate-task-modal__project-row">
                                <div className="edit-task-modal__project">
                                    <div className="cw-section-label">
                                        Project name
                                        <span className="cw-required-asterisk">*</span>
                                    </div>
                                    <div
                                        className={`edit-task-modal__project-select${projectNameError ? ' edit-task-modal__project-select--error' : ''}`}
                                    >
                                        <DropdownMenu
                                            className="edit-task-modal-dropdown"
                                            selectType="single-select"
                                            showLabel={false}
                                            size="medium"
                                            placeholder="Select a project"
                                            text={selectedProjectName || undefined}
                                            panelLayout="overlay"
                                            open={projectDropdownOpen}
                                            onOpenChange={setProjectDropdownOpen}
                                            searchable
                                            searchMode="trigger"
                                            searchEmptyText="No matching projects"
                                            items={projectNames.map((name) => ({
                                                label: name,
                                                state: name === selectedProjectName ? 'selected' : 'default',
                                                onClick: () => selectProject(name),
                                            }))}
                                            footer={(
                                                <div className="edit-task-modal-create-project">
                                                    <Input
                                                        className="edit-task-modal-field"
                                                        showLabel={false}
                                                        showHelperText={false}
                                                        placeholder="Create new project name"
                                                        value={newProjectName}
                                                        onChange={(e) => setNewProjectName(sanitizeProjectName(e.target.value))}
                                                        onKeyDown={(e) => {
                                                            if (e.key !== 'Enter') return;
                                                            e.stopPropagation();
                                                            createProjectFromInput();
                                                        }}
                                                    />
                                                    <Button variant="primary" onClick={createProjectFromInput}>
                                                        Add
                                                    </Button>
                                                </div>
                                            )}
                                        />
                                    </div>
                                    {projectNameError && (
                                        <div className="cw-field-error">Project name is required</div>
                                    )}
                                </div>

                                {showTypeFacts && (
                                    <div className="duplicate-task-modal__facts">
                                        <span className="duplicate-task-modal__fact-label">
                                            Task Type
                                        </span>
                                        <span className="duplicate-task-modal__fact-value">
                                            {getTaskTypeShortLabel(sourceTask?.task_type)}
                                        </span>
                                        {isClassification && (
                                            <>
                                                <span className="duplicate-task-modal__fact-label">
                                                    Can one example have more than one label?
                                                </span>
                                                <span className="duplicate-task-modal__fact-value">
                                                    {sourceTask?.is_multilabel ? 'Yes' : 'No'}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {(showLabelsSection || showFallbackSection) && (
                            <div className="cw-labels-fallback-row">
                                {showLabelsSection && (
                                    <TaskLabelsField
                                        taskType={baseTaskType}
                                        labels={displayLabels}
                                        onPatch={() => {}}
                                        readOnly
                                    />
                                )}
                                {showFallbackSection && (
                                    <FallbackLabelSelect
                                        labels={fallbackOptionLabels}
                                        classificationType={classificationType}
                                        value={fallbackLabel}
                                        onChange={(next) => {
                                            setFallbackLabel(next);
                                            if (next) setFallbackLabelError(null);
                                        }}
                                        error={fallbackLabelError}
                                    />
                                )}
                            </div>
                        )}

                        <div className="edit-task-modal__languages">
                            <LanguageSelector
                                value={taskLanguages || []}
                                onChange={(languages) => {
                                    setTaskLanguages(languages);
                                    setTaskLanguagesError(false);
                                }}
                                hasError={taskLanguagesError}
                                required={true}
                            />
                        </div>

                        {showGoalsSection && (
                            <>
                                <div className="cw-goals-section">
                                    <h3 className="cw-goals-section__title">
                                        Define &amp; Prioritize Business Goals
                                    </h3>
                                    <p className="cw-goals-section__subtitle">
                                        Drag and drop to rank your priorities. specific.ai will
                                        optimize the model based on these trade-offs.
                                    </p>
                                    <PriorityRanking order={priorityOrder} onChange={setPriorityOrder} />
                                </div>

                                <ConstraintsEditor
                                    constraints={constraints}
                                    onChange={(next) => {
                                        setConstraints(next);
                                        if (constraintErrors) {
                                            const collected = collectConstraintErrors(next);
                                            const stillInvalid = {};
                                            next.forEach((c) => {
                                                if (constraintErrors[c.id] && collected[c.id]) {
                                                    stillInvalid[c.id] = collected[c.id];
                                                }
                                            });
                                            setConstraintErrors(
                                                Object.keys(stillInvalid).length ? stillInvalid : null
                                            );
                                        }
                                    }}
                                    isNerTask={isNer}
                                    entityTypeOptions={displayLabels}
                                    errors={constraintErrors}
                                />
                            </>
                        )}
                    </div>
                </div>
                <ScrollBar
                    containerRef={scrollBodyRef}
                    className="edit-task-modal__scrollbar"
                />
            </div>
            <div className="edit-task-modal__footer">
                <Button variant="secondary" size="medium" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button variant="primary" size="medium" disableWhenReadOnly onClick={handleDuplicateTask}>
                    Duplicate
                </Button>
            </div>
        </SparkModalPortal>
    );
};
